import { useEffect, useMemo, useRef, useState } from 'react';

import { useFeature } from '@/features/feature-flags';

import { getSettingsMenuContext } from './settings-menu-context';
import { settingsMenuConfig } from './settings-menu.config';
import { searchSettings, type SettingsSearchGroup } from './settings-search';
import { getSettingsSearchBoosts } from './settings-search.acceptance';
import {
  settingsSearchEntries,
  settingsSearchProviders,
  type SettingsSearchEntry,
  type SettingsSearchProvider,
} from './settings-search.config';

/**
 * Search static settings entries immediately and reload entries from async
 * providers whenever a new search opens. A provider keeps its previous results
 * until its replacement finishes loading, so the open settings window does not
 * show a partially updated search index. `providersLoading` stays true until
 * every provider finishes, allowing callers to delay the empty state.
 */
export const useSettingsSearch = (
  query: string
): { groups: SettingsSearchGroup[]; providersLoading: boolean } => {
  // useFeature replaces hasFeature after a flag refresh so providers and filtered groups recompute
  const hasFeature = useFeature();
  const [providerEntriesByProvider, setProviderEntriesByProvider] = useState<
    Map<SettingsSearchProvider, SettingsSearchEntry[]>
  >(() => new Map());
  const [settledProviders, setSettledProviders] = useState(0);
  const providerRunRef = useRef(0);
  const searching = query.trim().length > 0;

  useEffect(() => {
    if (!searching) return;

    const run = ++providerRunRef.current;
    setSettledProviders(0);
    // Passing hasFeature here makes a flag refresh rerun the effect and reload feature-gated provider entries
    const ctx = getSettingsMenuContext({ hasFeature });

    const replaceProviderEntries = (
      provider: SettingsSearchProvider,
      entries: SettingsSearchEntry[]
    ) => {
      if (providerRunRef.current !== run) return;
      setProviderEntriesByProvider((previous) => {
        const next = new Map(previous);
        next.set(provider, entries);
        return next;
      });
    };

    for (const provider of settingsSearchProviders) {
      provider
        .getEntries(ctx)
        .then((entries) => replaceProviderEntries(provider, entries))
        .catch((error) => {
          console.error('Settings search provider failed:', error);
        })
        .finally(() => {
          if (providerRunRef.current === run) {
            setSettledProviders((count) => count + 1);
          }
        });
    }

    return () => {
      if (providerRunRef.current === run) providerRunRef.current += 1;
    };
  }, [hasFeature, searching]);

  const providerEntries = useMemo(
    () => [...providerEntriesByProvider.values()].flat(),
    [providerEntriesByProvider]
  );

  const groups = useMemo(
    () =>
      searching
        ? searchSettings(
            query,
            [...settingsSearchEntries, ...providerEntries],
            getSettingsMenuContext({ hasFeature }),
            settingsMenuConfig,
            // previously-accepted entries win score ties for similar queries
            getSettingsSearchBoosts(query)
          )
        : [],
    // Recompute the cached groups when a flag refresh replaces hasFeature
    [hasFeature, searching, query, providerEntries]
  );

  return {
    groups,
    providersLoading:
      searching && settledProviders < settingsSearchProviders.length,
  };
};
